import { h } from '@omni-torn/ui';

/** Small pieces the calculators share (pages/calc.ts, pages/calc-more.ts). What you type is kept in this browser only. */

export function load<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    return raw ? { ...fallback, ...(JSON.parse(raw) as T) } : fallback;
  } catch {
    return fallback;
  }
}

export function save(key: string, value: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* this visit only */
  }
}

export function tile(label: string, value: string, cls = '', data?: string): HTMLElement {
  return h('div', { class: `tile ${cls}`.trim() }, h('div', { class: 'tile-label' }, label), h('div', { class: 'tile-value', ...(data !== undefined ? { 'data-value': data } : {}) }, value));
}

export function numberInput(label: string, value: number, opts: { min?: number; max?: number; step?: number | 'any'; id?: string } = {}): HTMLInputElement {
  return h('input', { type: 'number', value: String(value), 'aria-label': label, class: 'num-input', inputmode: 'decimal', ...(opts.id ? { id: opts.id } : {}), ...(opts.min !== undefined ? { min: String(opts.min) } : {}), ...(opts.max !== undefined ? { max: String(opts.max) } : {}), ...(opts.step ? { step: String(opts.step) } : {}) }) as HTMLInputElement;
}

/** A number from an input, kept within [min, max] (a blank or bad value → `fallback`). */
export function readNum(el: HTMLInputElement, min: number, max: number, fallback = min): number {
  const n = Number(el.value);
  return el.value.trim() === '' || !Number.isFinite(n) ? fallback : Math.max(min, Math.min(max, n));
}

/** A labelled field for a .calc-grid. */
export const field = (label: string, el: HTMLElement, id?: string): HTMLElement => h('label', id ? { for: id } : {}, label, el);

export function select(label: string, options: ReadonlyArray<{ value: string; label: string }>, value: string, id?: string): HTMLSelectElement {
  return h('select', { 'aria-label': label, ...(id ? { id } : {}) }, ...options.map((o) => h('option', { value: o.value, ...(o.value === value ? { selected: true } : {}) }, o.label))) as HTMLSelectElement;
}

export function checkbox(label: string, on: boolean, id: string, change: (on: boolean) => void): HTMLElement {
  const c = h('input', { type: 'checkbox', id, ...(on ? { checked: true } : {}) }) as HTMLInputElement;
  c.addEventListener('change', () => change(c.checked));
  return h('label', { class: 'check' }, c, ` ${label}`);
}

/** Runs a click's work with the button disabled and a status line ("Asking Torn…", then the error if any). */
export async function busy(btn: HTMLElement, status: HTMLElement, what: string, run: () => Promise<void>): Promise<void> {
  btn.setAttribute('disabled', '');
  status.textContent = what;
  try {
    await run();
  } catch (err) {
    status.textContent = (err as Error).message;
  } finally {
    btn.removeAttribute('disabled');
  }
}

/** "Confirmed" / "estimate" tags on a calculator's notes, so a guess never reads as fact. */
export const sure = (text: string): HTMLElement => h('li', {}, h('strong', { class: 't-green' }, 'Torn’s own: '), text);
export const guess = (text: string): HTMLElement => h('li', {}, h('strong', { class: 't-amber' }, 'Estimate: '), text);
export const howSure = (...items: HTMLElement[]): HTMLElement => h('ul', { class: 'muted small calc-sources', 'data-calc-sources': '' }, ...items);
